// src/admin/AdminEspecialistasApostas.js
import React, { useEffect, useMemo, useState } from "react";
import { db, storage } from "../firebase";
import {
  doc, getDoc, setDoc,
} from "firebase/firestore";
import {
  ref as storageRef, uploadString, getDownloadURL
} from "firebase/storage";

// onde guardamos season e especialistas
const REF_CONFIG        = doc(db, "termometreiro", "config");        // { season: "2026" }
const REF_ESPECIALISTAS = doc(db, "termometreiro", "especialistas"); // { uids: [] }

// util
function normalizarListaUIDs(text) {
  const linhas = (text || "")
    .split("\n")
    .map(s => s.trim())
    .filter(Boolean);
  const vistos = new Set();
  const saida = [];
  for (const uid of linhas) {
    if (!vistos.has(uid)) {
      vistos.add(uid);
      saida.push(uid);
    }
  }
  return saida;
}

export default function AdminEspecialistasApostas() {
  // UI state
  const [season, setSeason] = useState("2026");
  const [uidsText, setUidsText] = useState("");
  const [savingSeason, setSavingSeason] = useState(false);
  const [savingList, setSavingList] = useState(false);
  const [building, setBuilding] = useState(false);
  const [msg, setMsg] = useState("");
  const [downloadURL, setDownloadURL] = useState("");

  // carregar config/season + especialistas/uids
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const [cfg, esp] = await Promise.all([getDoc(REF_CONFIG), getDoc(REF_ESPECIALISTAS)]);
        if (!ativo) return;
        if (cfg.exists() && cfg.data()?.season) {
          setSeason(String(cfg.data().season));
        }
        if (esp.exists() && Array.isArray(esp.data()?.uids)) {
          setUidsText(esp.data().uids.join("\n"));
        }
      } catch (e) {
        console.error("Erro ao carregar config/especialistas:", e);
        setMsg("Erro ao carregar configuração.");
      }
    })();
    return () => { ativo = false; };
  }, []);

  async function salvarSeason() {
    setSavingSeason(true);
    setMsg("");
    try {
      await setDoc(
        REF_CONFIG,
        { season: String(season), updatedAt: new Date().toISOString() },
        { merge: true }
      );
      setMsg("Temporada guardada.");
    } catch (e) {
      console.error(e);
      setMsg("Erro ao guardar temporada.");
    } finally {
      setSavingSeason(false);
    }
  }

  async function salvarUids() {
    setSavingList(true);
    setMsg("");
    try {
      const uids = normalizarListaUIDs(uidsText);
      await setDoc(
        REF_ESPECIALISTAS,
        { uids, updatedAt: new Date().toISOString() },
        { merge: true }
      );
      setMsg(`Lista de especialistas guardada (${uids.length}).`);
    } catch (e) {
      console.error(e);
      setMsg("Erro ao guardar lista.");
    } finally {
      setSavingList(false);
    }
  }

  // ---- fetchers -----------------------------------------------------------

  async function fetchPerfil(uid) {
    try {
      const s = await getDoc(doc(db, "usuarios", uid));
      if (s.exists()) {
        const d = s.data() || {};
        return {
          uid,
          nome: d.nome || "",
          sobrenome: d.sobrenome || "",
          estado: d.estado || "",
          fotoPerfil: d.fotoPerfil || "",
          criadoEm: d.criadoEm || null,
          especialista: !!d.especialista,
          // >>> adicionados:
          linkParaSeguir: d.linkParaSeguir || "",
          denominacao: d.denominacao || ""
        };
      }
    } catch {}
    return {
      uid,
      nome: "",
      sobrenome: "",
      estado: "",
      fotoPerfil: "",
      criadoEm: null,
      especialista: false,
      // >>> manter chaves presentes mesmo vazias
      linkParaSeguir: "",
      denominacao: ""
    };
  }

  async function fetchApostas(uid, seasonStr) {
    // doc: /usuarios/{uid}/snapDropdown{SEASON}/dados
    try {
      const s = await getDoc(doc(db, "usuarios", uid, `snapDropdown${seasonStr}`, "dados"));
      return s.exists() ? (s.data() || {}) : {};
    } catch (e) {
      console.warn("Falha ao ler apostas de", uid, e);
      return {};
    }
  }

  // ---- builder principal --------------------------------------------------

  async function gerarJS() {
  setBuilding(true);
  setMsg("");
  setDownloadURL("");

  try {
    // confirma season e uids atuais salvos
    const [cfgSnap, espSnap] = await Promise.all([
      getDoc(REF_CONFIG),
      getDoc(REF_ESPECIALISTAS)
    ]);

    const currSeason =
      (cfgSnap.exists() && cfgSnap.data()?.season)
        ? String(cfgSnap.data().season)
        : String(season);

    const uids =
      (espSnap.exists() && Array.isArray(espSnap.data()?.uids))
        ? espSnap.data().uids
        : normalizarListaUIDs(uidsText);

    // carrega perfis + apostas
    const perfis = [];
    const dadosPorEspecialista = {};

    await Promise.all(
      uids.map(async (uid) => {
        const [perfil, apostas] = await Promise.all([
          fetchPerfil(uid),
          fetchApostas(uid, currSeason),
        ]);
        perfis.push(perfil);
        dadosPorEspecialista[uid] = apostas;
      })
    );

    const payload = {
      version: 1,
      generatedAt: new Date().toISOString(),
      season: currSeason,
      especialistas: perfis,
      dadosPorEspecialista,
    };

    const json = JSON.stringify(payload, null, 2);
    const jsBundle = `window.__ESPECIALISTAS_APOSTAS__ = ${json};`;

    // ==================================
    // 1️⃣ JS GRANDE (igual ao atual)
    // ==================================
    const pathJs = `public/termometreiro/especialistas-apostas-${currSeason}.js`;
    const refJs = storageRef(storage, pathJs);

    await uploadString(refJs, jsBundle, "raw", {
      contentType: "application/javascript; charset=utf-8",
      cacheControl: "public, max-age=60",
    });

    // ==================================
    // 2️⃣ NOVO: VERSION PEQUENO
    // ==================================
    const versionPayload = {
      version: payload.version,
      generatedAt: payload.generatedAt,
      season: payload.season,
    };

    const versionJson = JSON.stringify(versionPayload);

    const pathVer = `public/termometreiro/especialistas-apostas-version-${currSeason}.json`;
    const refVer = storageRef(storage, pathVer);

    await uploadString(refVer, versionJson, "raw", {
      contentType: "application/json; charset=utf-8",
      cacheControl: "public, max-age=60",
    });

    const url = await getDownloadURL(refJs);
    setDownloadURL(url);
    setMsg("JS e version de especialistas gerados com sucesso.");
  } catch (e) {
    console.error("Erro ao gerar JS de especialistas:", e);
    setMsg("Erro ao gerar JS. Veja o console.");
  } finally {
    setBuilding(false);
  }
}


  // ------------------------------------------------------------------------

  const totalUids = useMemo(() => normalizarListaUIDs(uidsText).length, [uidsText]);

  return (
    <div style={{ marginTop: 24 }}>
      <h2>Admin — Especialistas Apostas</h2>

      {/* Temporada */}
      <div style={{ margin: "16px 0", padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
        <h3 style={{ marginTop: 0 }}>Temporada</h3>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <label><strong>Season:</strong></label>
          <input
            type="text"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            style={{ width: 120 }}
          />
          <button className="btn" onClick={salvarSeason} disabled={savingSeason}>
            {savingSeason ? "Guardando..." : "Guardar temporada"}
          </button>
        </div>
      </div>

      {/* UIDs especialistas */}
      <div style={{ margin: "16px 0", padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
        <h3 style={{ marginTop: 0 }}>Especialistas (UIDs)</h3>
        <p style={{ marginTop: 0 }}>Cole <em>um UID por linha</em>. Apenas estes UIDs terão as apostas exportadas.</p>
        <textarea
          rows={10}
          style={{ width: "100%", boxSizing: "border-box" }}
          value={uidsText}
          onChange={(e) => setUidsText(e.target.value)}
          placeholder={"uid_1\nuid_2\nuid_3"}
        />
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8 }}>
          <button className="btn" onClick={salvarUids} disabled={savingList}>
            {savingList ? "Guardando..." : "Guardar lista"}
          </button>
          <span>Total de UIDs: <strong>{totalUids}</strong></span>
        </div>
      </div>

      {/* Ação principal */}
      <div style={{ margin: "16px 0", padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
        <h3 style={{ marginTop: 0 }}>Gerar JS (Especialistas Apostas)</h3>
        <p style={{ marginTop: 0 }}>
          Lê <code>/usuarios/&lt;uid&gt;/snapDropdown{season}/dados</code> para cada especialista e publica um bundle JS com todas as informações.
        </p>
        <button className="btn" onClick={gerarJS} disabled={building}>
          {building ? "A gerar..." : "Gerar JS (Especialistas Apostas)"}
        </button>

        {downloadURL && (
          <p style={{ marginTop: 10 }}>
            <a href={downloadURL} target="_blank" rel="noreferrer">Abrir especialistas-apostas-{season}.js</a>
          </p>
        )}
      </div>

      {msg && <p style={{ marginTop: 8 }}>{msg}</p>}
    </div>
  );
}
